import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, confirmPasswordReset, signOut, onAuthStateChanged, User, sendEmailVerification, updateProfile, updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential, applyActionCode } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db, actionCodeSettings } from '../firebase';
import { detectUserTimezone } from '../utils/timezoneUtils';
import { matchStudentToTeacher } from '../services/studentService';
// ============================================================================
// VERIFICATION EMAIL SEND GUARD
// ============================================================================
const VERIFICATION_EMAIL_COOLDOWN_MS = 60000; // 60 seconds
/**
 * Check if enough time has passed since last verification email send
 * Returns true if email can be sent, false if still in cooldown
 */
const canSendVerificationEmail = (userEmail: string): boolean => {
  const storageKey = `lastVerificationEmailSent_${userEmail}`;
  const lastSentStr = localStorage.getItem(storageKey);
  if (!lastSentStr) {
    return true; // Never sent before
  }
  const lastSent = parseInt(lastSentStr, 10);
  const timeSinceLastSend = Date.now() - lastSent;
  return timeSinceLastSend > VERIFICATION_EMAIL_COOLDOWN_MS;
};
/**
 * Get remaining cooldown time in seconds
 */
const getRemainingCooldown = (userEmail: string): number => {
  const storageKey = `lastVerificationEmailSent_${userEmail}`;
  const lastSentStr = localStorage.getItem(storageKey);
  if (!lastSentStr) {
    return 0;
  }
  const lastSent = parseInt(lastSentStr, 10);
  const timeSinceLastSend = Date.now() - lastSent;
  const remainingMs = VERIFICATION_EMAIL_COOLDOWN_MS - timeSinceLastSend;
  return Math.max(0, Math.ceil(remainingMs / 1000));
};
/**
 * Record successful verification email send
 */
const recordVerificationEmailSent = (userEmail: string): void => {
  const storageKey = `lastVerificationEmailSent_${userEmail}`;
  localStorage.setItem(storageKey, Date.now().toString());
};
// ============================================================================
// AUTH CONTEXT TYPES
// ============================================================================
interface AuthContextType {
  currentUser: User | null;
  userData: any;
  userType: string | null;
  loading: boolean;
  signUp: (email: string, password: string, userType: string) => Promise<any>;
  registerTeacher: (email: string, password: string, firstName: string, lastName: string, location: string, city: string, state: string) => Promise<any>;
  registerStudent: (email: string, password: string, firstName: string, lastName: string) => Promise<any>;
  login: (email: string, password: string, userType?: string) => Promise<any>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmReset: (oobCode: string, newPassword: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUserProfile: (data: any) => Promise<void>;
  updateUserEmail: (email: string, password: string) => Promise<void>;
  updateUserPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  verifyEmail: (actionCode: string) => Promise<void>;
  setVerificationInProgress: (inProgress: boolean) => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [userType, setUserType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // STEP 3: Track whether logout was explicitly called to distinguish from cross-tab sign-out
  const isExplicitLogout = useRef(false);
  // Track previous user to detect cross-tab sign-out
  const previousUserRef = useRef<User | null>(null);
  // EMAIL VERIFICATION FIX: Guard to prevent cross-tab detection during email verification
  // When true, the onAuthStateChanged listener will NOT redirect to '/' even if it detects
  // a user→null transition (which can happen during applyActionCode + reload + getIdToken)
  const isVerificationInProgressRef = useRef(false);
  const setVerificationInProgress = (inProgress: boolean) => {
    console.log('[Auth] setVerificationInProgress:', inProgress);
    isVerificationInProgressRef.current = inProgress;
  };
  const checkIfAccountDeleted = async (user: User): Promise<boolean> => {
    try {
      console.log('[Auth Guard] Checking if account is deleted for:', user.uid);
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.deleted === true) {
          console.log('[Auth Guard] Account marked as deleted in users collection');
          return true;
        }
      }
      const storedUserType = localStorage.getItem('userType');
      if (storedUserType === 'teacher') {
        const teacherRef = doc(db, 'teachers', user.uid);
        const teacherDoc = await getDoc(teacherRef);
        if (teacherDoc.exists() && teacherDoc.data().deleted === true) {
          console.log('[Auth Guard] Account marked as deleted in teachers collection');
          return true;
        }
      } else if (storedUserType === 'student') {
        const studentRef = doc(db, 'students', user.uid);
        const studentDoc = await getDoc(studentRef);
        if (studentDoc.exists() && studentDoc.data().deleted === true) {
          console.log('[Auth Guard] Account marked as deleted in students collection');
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('[Auth Guard] Error checking deleted status:', error);
      return false;
    }
  };
  const forceLogoutDeletedAccount = async () => {
    console.log('[Auth Guard] Forcing logout for deleted account');
    try {
      await signOut(auth);
      localStorage.clear();
      setCurrentUser(null);
      setUserData(null);
      setUserType(null);
      window.location.href = '/?message=account_deleted';
    } catch (error) {
      console.error('[Auth Guard] Error during forced logout:', error);
      window.location.href = '/';
    }
  };
  // FIXED: Updated signUp to ALWAYS use actionCodeSettings
  async function signUp(email: string, password: string, type: string) {
    try {
      console.log('[SignUp] Starting signup for type:', type);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const userRef = doc(db, type === 'teacher' ? 'teachers' : 'students', user.uid);
      await setDoc(userRef, {
        email: email.toLowerCase(),
        userType: type,
        createdAt: new Date(),
        deleted: false
      });
      // CRITICAL FIX: Always pass actionCodeSettings to ensure app deep link
      console.log('[SignUp] Sending verification email with actionCodeSettings');
      console.log('[SignUp] Email will link to:', actionCodeSettings.url);
      await sendEmailVerification(user, actionCodeSettings);
      console.log('[SignUp] Verification email sent successfully');
      // Record send for cooldown tracking
      recordVerificationEmailSent(email);
      localStorage.setItem('userType', type);
      setUserType(type);
      return userCredential;
    } catch (error) {
      console.error('[SignUp] Error signing up:', error);
      throw error;
    }
  }
  // FIXED: Updated registerTeacher to ALWAYS use actionCodeSettings
  async function registerTeacher(email: string, password: string, firstName: string, lastName: string, location: string, city: string, state: string) {
    try {
      console.log('[RegisterTeacher] Starting teacher registration');
      console.log('[RegisterTeacher] Email:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('[RegisterTeacher] User created:', user.uid);
      const displayName = `${firstName} ${lastName}`.trim();
      await updateProfile(user, {
        displayName
      });
      console.log('[RegisterTeacher] Profile updated');
      const detectedTimezone = detectUserTimezone();
      console.log('[RegisterTeacher] Detected timezone:', detectedTimezone);
      const teacherRef = doc(db, 'teachers', user.uid);
      await setDoc(teacherRef, {
        uid: user.uid,
        email: email.toLowerCase(),
        firstName,
        lastName,
        displayName,
        location,
        city,
        state,
        userType: 'teacher',
        isVerified: false,
        deleted: false,
        timeZone: detectedTimezone || 'America/New_York',
        timezoneAutoDetected: !!detectedTimezone,
        // 30-day free trial — managed in Firestore, no payment required
        subscription: {
          status: 'trial',
          trialStartedAt: new Date().toISOString(),
          trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          paddleSubscriptionId: null,
          paddleCustomerId: null,
          currentPeriodStart: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
          plan: {
            name: 'Teacher Monthly Plan',
            price: 9.99,
            interval: 'month',
            currency: 'USD'
          }
        },
        onboardingCompleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('[RegisterTeacher] Teacher document created');
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        uid: user.uid,
        email: email.toLowerCase(),
        displayName,
        firstName,
        lastName,
        userType: 'teacher',
        deleted: false,
        phone: '',
        address: {
          street: '',
          city,
          state,
          zipCode: '',
          country: 'United States'
        },
        // 30-day free trial — must be in users collection for auth listener
        subscription: {
          status: 'trial',
          trialStartedAt: new Date().toISOString(),
          trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          paddleSubscriptionId: null,
          paddleCustomerId: null,
          currentPeriodStart: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
          plan: {
            name: 'Teacher Monthly Plan',
            price: 9.99,
            interval: 'month',
            currency: 'USD'
          }
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      console.log('[RegisterTeacher] User document created');
      localStorage.setItem('userType', 'teacher');
      setUserType('teacher');
      localStorage.setItem('teacherName', displayName);
      localStorage.setItem('teacherFirstName', firstName);
      localStorage.setItem('teacherLastName', lastName);
      localStorage.setItem('teacherEmail', email);
      localStorage.setItem('teacherLocation', location);
      localStorage.setItem('teacherCity', city);
      localStorage.setItem('teacherState', state);
      // CRITICAL FIX: Check cooldown before sending
      if (!canSendVerificationEmail(email)) {
        const remaining = getRemainingCooldown(email);
        console.log(`[RegisterTeacher] Cooldown active, ${remaining}s remaining`);
        throw new Error(`Please wait ${remaining} seconds before requesting another verification email.`);
      }
      // CRITICAL FIX: Always pass actionCodeSettings to ensure app deep link
      try {
        console.log('[RegisterTeacher] Sending verification email with actionCodeSettings');
        console.log('[RegisterTeacher] Email will link to:', actionCodeSettings.url);
        console.log('[RegisterTeacher] handleCodeInApp:', actionCodeSettings.handleCodeInApp);
        await sendEmailVerification(user, actionCodeSettings);
        // Record successful send
        recordVerificationEmailSent(email);
        console.log('[RegisterTeacher] Verification email sent successfully');
        console.log('[RegisterTeacher] User should receive link to:', actionCodeSettings.url);
      } catch (emailError: any) {
        console.error('[RegisterTeacher] Error sending verification email:', emailError);
        console.error('[RegisterTeacher] Error code:', emailError.code);
        // Don't throw - allow registration to complete
      }
      return userCredential;
    } catch (error: any) {
      console.error('[RegisterTeacher] Error:', error);
      throw error;
    }
  }
  // FIXED: Updated registerStudent to ALWAYS use actionCodeSettings
  async function registerStudent(email: string, password: string, firstName: string, lastName: string) {
    try {
      console.log('[RegisterStudent] Starting student registration');
      console.log('[RegisterStudent] Email:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('[RegisterStudent] User created:', user.uid);
      const displayName = `${firstName} ${lastName}`.trim();
      await updateProfile(user, {
        displayName
      });
      console.log('[RegisterStudent] Profile updated');
      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        uid: user.uid,
        email: email.toLowerCase(),
        firstName,
        lastName,
        displayName,
        userType: 'student',
        deleted: false,
        onboardingStep: 'email-verification',
        onboardingComplete: false,
        phone: '',
        instrument: '',
        skillLevel: '',
        lessonPreferences: {},
        timezone: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        emailVerified: false
      });
      console.log('[RegisterStudent] User document created');
      // TEACHER-STUDENT CONNECTION: Match this student to any teacher who already added them
      try {
        const matchedTeacherIds = await matchStudentToTeacher(email.toLowerCase(), user.uid);
        if (matchedTeacherIds.length > 0) {
          console.log('[RegisterStudent] Matched to teachers:', matchedTeacherIds);
          await updateDoc(userRef, {
            teacherIds: matchedTeacherIds,
            updatedAt: new Date().toISOString()
          });
          console.log('[RegisterStudent] teacherIds saved to user document');
        } else {
          console.log('[RegisterStudent] No teacher matches found');
        }
      } catch (matchError) {
        console.warn('[RegisterStudent] Teacher matching failed (non-blocking):', matchError);
        // Do NOT throw — matching failure must never block student signup
      }
      // CRITICAL FIX: Always pass actionCodeSettings to ensure app deep link
      try {
        console.log('[RegisterStudent] Sending verification email with actionCodeSettings');
        console.log('[RegisterStudent] Email will link to:', actionCodeSettings.url);
        await sendEmailVerification(user, actionCodeSettings);
        console.log('[RegisterStudent] Verification email sent successfully');
      } catch (emailError) {
        console.error('[RegisterStudent] Error sending verification email:', emailError);
      }
      localStorage.setItem('userType', 'student');
      setUserType('student');
      localStorage.setItem('studentEmail', email);
      localStorage.setItem('studentName', displayName);
      localStorage.setItem('studentFirstName', firstName);
      localStorage.setItem('studentLastName', lastName);
      return userCredential;
    } catch (error: any) {
      console.error('[RegisterStudent] Error:', error);
      throw error;
    }
  }
  async function login(email: string, password: string, type?: string) {
    try {
      console.log('=== LOGIN DEBUG START ===');
      console.log('Email:', email);
      console.log('Type parameter received:', type);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('Firebase Auth successful, UID:', user.uid);
      const isDeleted = await checkIfAccountDeleted(user);
      if (isDeleted) {
        console.log('[Login] Account is deleted, signing out');
        await signOut(auth);
        throw new Error('This account has been deleted. Please contact support if you believe this is an error.');
      }
      const idTokenResult = await user.getIdTokenResult();
      const claimedUserType = idTokenResult.claims.userType as string | undefined;
      console.log('Custom claim userType:', claimedUserType);
      if (type) {
        console.log('Type validation enabled, expected type:', type);
        if (claimedUserType && claimedUserType !== type) {
          console.log('MISMATCH: Custom claim does not match expected type!');
          console.log('Expected:', type, 'Got:', claimedUserType);
          await signOut(auth);
          throw new Error(`Account type mismatch. Please use ${claimedUserType} login.`);
        }
        const userRef = doc(db, 'users', user.uid);
        console.log('Checking Firestore users collection for UID:', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const firestoreUserType = userDoc.data().userType;
          console.log('Firestore userType:', firestoreUserType);
          if (firestoreUserType !== type) {
            console.log('MISMATCH: Firestore userType does not match expected type!');
            console.log('Expected:', type, 'Got:', firestoreUserType);
            await signOut(auth);
            throw new Error(`No ${type} account found with this email`);
          }
          if (claimedUserType && claimedUserType !== firestoreUserType) {
            console.error('Custom claim does not match Firestore userType!');
            await signOut(auth);
            throw new Error('Account data inconsistency detected. Please contact support.');
          }
          console.log('Validation passed! Setting userType:', firestoreUserType);
          localStorage.setItem('userType', firestoreUserType);
          setUserType(firestoreUserType);
        } else {
          console.log('User document not found in users collection');
          if (type === 'teacher') {
            console.log('Checking legacy teachers collection');
            const teacherRef = doc(db, 'teachers', user.uid);
            const teacherDoc = await getDoc(teacherRef);
            if (teacherDoc.exists()) {
              console.log('Found in teachers collection');
              localStorage.setItem('userType', 'teacher');
              setUserType('teacher');
            } else {
              console.log('Not found in teachers collection either, signing out');
              await signOut(auth);
              throw new Error(`No ${type} account found with this email`);
            }
          } else if (type === 'student') {
            console.log('Checking legacy students collection');
            const studentRef = doc(db, 'students', user.uid);
            const studentDoc = await getDoc(studentRef);
            if (studentDoc.exists()) {
              console.log('Found in students collection');
              const studentData = studentDoc.data();
              if (studentData.userType === 'student' || !studentData.userType) {
                console.log('Confirmed as student account in legacy collection');
                localStorage.setItem('userType', 'student');
                setUserType('student');
              } else {
                console.log('Legacy document has wrong userType:', studentData.userType);
                await signOut(auth);
                throw new Error(`No ${type} account found with this email`);
              }
            } else {
              console.log('Not found in students collection either, signing out');
              await signOut(auth);
              throw new Error(`No ${type} account found with this email`);
            }
          } else {
            console.log('Unknown account type, signing out');
            await signOut(auth);
            throw new Error(`No ${type} account found with this email`);
          }
        }
      } else {
        console.log('No type parameter provided, determining from claims/Firestore');
        let determinedType = claimedUserType;
        if (!determinedType) {
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            determinedType = userDoc.data().userType;
          } else {
            const teacherRef = doc(db, 'teachers', user.uid);
            const teacherDoc = await getDoc(teacherRef);
            if (teacherDoc.exists()) {
              determinedType = 'teacher';
            }
          }
        }
        if (determinedType) {
          localStorage.setItem('userType', determinedType);
          setUserType(determinedType);
        }
      }
      console.log('=== LOGIN DEBUG END - SUCCESS ===');
      return userCredential;
    } catch (error) {
      console.error('=== LOGIN DEBUG END - ERROR ===');
      console.error('Error logging in:', error);
      throw error;
    }
  }
  async function logout() {
    try {
      // STEP 3: Mark this as an explicit logout so onAuthStateChanged doesn't double-redirect
      isExplicitLogout.current = true;
      await signOut(auth);
      // STEP 4: Comprehensive localStorage cleanup of all auth-related keys
      const authKeysToRemove = ['userType', 'teacherName', 'teacherFirstName', 'teacherLastName', 'teacherEmail', 'teacherLocation', 'teacherCity', 'teacherState', 'studentEmail', 'studentName', 'studentFirstName', 'studentLastName', 'scrollToSection', 'scrollToTop'];
      authKeysToRemove.forEach(key => localStorage.removeItem(key));
      // Also remove any profile image keys (prefixed with maestroConnect_)
      const keysToCheck = Object.keys(localStorage);
      keysToCheck.forEach(key => {
        if (key.startsWith('maestroConnect_') || key.startsWith('lastVerificationEmailSent_')) {
          localStorage.removeItem(key);
        }
      });
      setUserType(null);
      setUserData(null);
    } catch (error) {
      console.error('Error logging out:', error);
      isExplicitLogout.current = false;
      throw error;
    }
  }
  async function resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email, {
        url: 'https://maestroconnect.io/reset-password',
        handleCodeInApp: true
      });
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  }
  async function confirmReset(oobCode: string, newPassword: string) {
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
    } catch (error) {
      console.error('Error confirming password reset:', error);
      throw error;
    }
  }
  // FIXED: Updated sendVerificationEmail to ALWAYS use actionCodeSettings
  async function sendVerificationEmail() {
    try {
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      const userEmail = currentUser.email || '';
      console.log('[Resend] Attempting to send verification email');
      console.log('[Resend] User email:', userEmail);
      // Check cooldown before sending
      if (!canSendVerificationEmail(userEmail)) {
        const remaining = getRemainingCooldown(userEmail);
        console.log(`[Resend] Cooldown active, ${remaining}s remaining`);
        throw new Error(`Please wait ${remaining} seconds before requesting another verification email.`);
      }
      // CRITICAL FIX: Always pass actionCodeSettings to ensure app deep link
      console.log('[Resend] Sending verification email with actionCodeSettings');
      console.log('[Resend] Email will link to:', actionCodeSettings.url);
      console.log('[Resend] handleCodeInApp:', actionCodeSettings.handleCodeInApp);
      await sendEmailVerification(currentUser, actionCodeSettings);
      // Record successful send
      recordVerificationEmailSent(userEmail);
      console.log('[Resend] Verification email sent successfully');
      console.log('[Resend] User should receive link to:', actionCodeSettings.url);
      return true;
    } catch (error: any) {
      console.error('[Resend] Error sending verification email:', error);
      if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many verification emails sent. Please try again later.');
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error. Please check your internet connection and try again.');
      } else if (error.message && error.message.includes('wait')) {
        throw error;
      } else {
        throw error;
      }
    }
  }
  async function verifyEmail(actionCode: string) {
    try {
      console.log('[VerifyEmail] Applying action code:', actionCode);
      await applyActionCode(auth, actionCode);
      console.log('[VerifyEmail] Action code applied successfully');
      if (currentUser) {
        console.log('[VerifyEmail] Refreshing user token and data');
        try {
          await currentUser.getIdToken(true);
          console.log('[VerifyEmail] Token refreshed successfully');
        } catch (tokenError) {
          console.error('[VerifyEmail] Error refreshing token:', tokenError);
        }
        try {
          await currentUser.reload();
          console.log('[VerifyEmail] User reloaded successfully');
          const updatedUser = auth.currentUser;
          if (updatedUser) {
            console.log('[VerifyEmail] Email verified status:', updatedUser.emailVerified);
            setCurrentUser(updatedUser);
          }
        } catch (reloadError) {
          console.error('[VerifyEmail] Error reloading user:', reloadError);
        }
      }
      return true;
    } catch (error: any) {
      console.error('[VerifyEmail] Error:', error);
      throw error;
    }
  }
  async function refreshUser(): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        if (!currentUser) {
          reject(new Error('No user is currently signed in'));
          return;
        }
        console.log('[RefreshUser] Refreshing user data');
        try {
          await currentUser.getIdToken(true);
          console.log('[RefreshUser] Token refreshed');
        } catch (tokenError) {
          console.error('[RefreshUser] Error refreshing token:', tokenError);
        }
        try {
          await currentUser.reload();
          console.log('[RefreshUser] User reloaded');
        } catch (reloadError) {
          console.error('[RefreshUser] Error reloading user:', reloadError);
          reject(reloadError);
          return;
        }
        const updatedUser = auth.currentUser;
        if (updatedUser) {
          console.log('[RefreshUser] Email verified status:', updatedUser.emailVerified);
          setCurrentUser(updatedUser);
          resolve();
        } else {
          console.error('[RefreshUser] User not found after reload');
          reject(new Error('User not found after reload'));
        }
      } catch (error) {
        console.error('[RefreshUser] Error:', error);
        reject(error);
      }
    });
  }
  async function updateUserProfile(data: any) {
    try {
      if (!currentUser) throw new Error('No user logged in');
      if (data.displayName || data.photoURL) {
        await updateProfile(currentUser, {
          displayName: data.displayName || currentUser.displayName,
          photoURL: data.photoURL || currentUser.photoURL
        });
      }
      if (userType) {
        const userRef = doc(db, userType === 'teacher' ? 'teachers' : 'students', currentUser.uid);
        await updateDoc(userRef, data);
        setUserData((prevData: any) => ({
          ...prevData,
          ...data
        }));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }
  async function updateUserEmail(email: string, password: string) {
    try {
      if (!currentUser) throw new Error('No user logged in');
      const credential = EmailAuthProvider.credential(currentUser.email!, password);
      await reauthenticateWithCredential(currentUser, credential);
      await updateEmail(currentUser, email);
      if (userType) {
        const userRef = doc(db, userType === 'teacher' ? 'teachers' : 'students', currentUser.uid);
        await updateDoc(userRef, {
          email: email.toLowerCase()
        });
        setUserData((prevData: any) => ({
          ...prevData,
          email: email.toLowerCase()
        }));
      }
    } catch (error) {
      console.error('Error updating email:', error);
      throw error;
    }
  }
  async function updateUserPassword(currentPassword: string, newPassword: string) {
    try {
      if (!currentUser) throw new Error('No user logged in');
      const credential = EmailAuthProvider.credential(currentUser.email!, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  }
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      // STEP 3: Detect cross-tab sign-out (user was previously authenticated, now null)
      const hadPreviousUser = previousUserRef.current !== null;
      previousUserRef.current = user;
      setCurrentUser(user);
      if (user) {
        // Reset explicit logout flag since user is authenticated
        isExplicitLogout.current = false;
        // Ensure loading is true while we fetch user data from Firestore
        // This prevents AuthGuard from rendering children before userData is ready
        setLoading(true);
        try {
          const isDeleted = await checkIfAccountDeleted(user);
          if (isDeleted) {
            console.log('[Auth State] Account is deleted, forcing logout');
            await forceLogoutDeletedAccount();
            setLoading(false);
            return;
          }
          const idTokenResult = await user.getIdTokenResult();
          const claimedUserType = idTokenResult.claims.userType as string | undefined;
          console.log('Auth state changed - custom claim userType:', claimedUserType);
          let finalUserType = claimedUserType;
          if (!finalUserType) {
            finalUserType = localStorage.getItem('userType');
            console.log('No custom claim, using localStorage:', finalUserType);
          }
          console.log('[Auth] finalUserType resolved to:', finalUserType);
          // If finalUserType is STILL not resolved, fall back to Firestore users/{uid}.userType
          if (!finalUserType) {
            console.log('[Auth] No userType from claims or localStorage, falling back to Firestore users/' + user.uid);
            const userRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const firestoreUserType = userDoc.data().userType as string | undefined;
              if (firestoreUserType) {
                finalUserType = firestoreUserType;
                console.log('[Auth] Resolved userType from Firestore:', finalUserType);
                // Cache it in localStorage for future use
                localStorage.setItem('userType', finalUserType);
              }
            }
            // If still no userType, try teachers collection as last resort
            if (!finalUserType) {
              console.log('[Auth] users doc missing or no userType, trying teachers/' + user.uid);
              const teacherRef = doc(db, 'teachers', user.uid);
              const teacherDoc = await getDoc(teacherRef);
              if (teacherDoc.exists()) {
                finalUserType = 'teacher';
                console.log('[Auth] Found teacher doc, setting userType to teacher');
                localStorage.setItem('userType', 'teacher');
              }
            }
          }
          if (finalUserType) {
            setUserType(finalUserType);
            const userRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userRef);
            console.log('[Auth] users/' + user.uid + ' exists:', userDoc.exists());
            if (userDoc.exists()) {
              const firestoreData = userDoc.data();
              console.log('[Auth] users doc userType:', firestoreData.userType, '| has subscription:', !!firestoreData.subscription);
              if (firestoreData.userType !== finalUserType) {
                console.error('UserType mismatch between claim/localStorage and Firestore!');
                await signOut(auth);
                setUserType(null);
                setUserData(null);
                return;
              }
              setUserData(firestoreData);
              console.log('[Auth] setUserData from users/' + user.uid);
            } else if (finalUserType === 'teacher') {
              console.log('[Auth] users doc missing, trying teachers/' + user.uid);
              const teacherRef = doc(db, 'teachers', user.uid);
              const teacherDoc = await getDoc(teacherRef);
              console.log('[Auth] teachers/' + user.uid + ' exists:', teacherDoc.exists());
              if (teacherDoc.exists()) {
                const teacherData = teacherDoc.data();
                console.log('[Auth] teacher doc has subscription:', !!teacherData.subscription);
                setUserData(teacherData);
                console.log('[Auth] setUserData from teachers/' + user.uid);
              } else {
                console.error('[Auth] Neither users nor teachers doc found for:', user.uid);
              }
            } else {
              console.log('[Auth] users doc missing and userType is not teacher:', finalUserType);
            }
          } else {
            console.error('[Auth] CRITICAL: Could not resolve userType from any source (claims, localStorage, Firestore) for uid:', user.uid);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        setUserType(null);
        setUserData(null);
        // STEP 3: If user was previously authenticated and this wasn't an explicit logout,
        // this is a cross-tab sign-out — force redirect to landing page
        // EMAIL VERIFICATION FIX: Also skip if verification is in progress
        if (hadPreviousUser && !isExplicitLogout.current && !isVerificationInProgressRef.current) {
          console.log('[Auth State] Cross-tab sign-out detected, redirecting to /');
          // Clean up localStorage for cross-tab case too
          localStorage.removeItem('userType');
          window.location.href = '/';
          return;
        }
        if (isVerificationInProgressRef.current) {
          console.log('[Auth State] Auth state null during verification — suppressed cross-tab redirect');
        }
        // Reset the explicit logout flag
        isExplicitLogout.current = false;
      }
      setLoading(false);
    });
    // STEP 6: Cross-tab sync via storage event listener
    // Detects when another tab removes 'userType' from localStorage (sign-out)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'userType' && event.newValue === null && event.oldValue !== null) {
        console.log('[Auth State] Cross-tab storage event: userType removed, forcing sign-out');
        // Another tab signed out — force this tab to sign out too
        signOut(auth).catch(err => console.error('[Auth State] Error during cross-tab sign-out:', err));
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  const value = {
    currentUser,
    userData,
    userType,
    loading,
    signUp,
    registerTeacher,
    registerStudent,
    login,
    logout,
    resetPassword,
    confirmReset,
    sendVerificationEmail,
    refreshUser,
    updateUserProfile,
    updateUserEmail,
    updateUserPassword,
    verifyEmail,
    setVerificationInProgress
  };
  return <AuthContext.Provider value={value} data-id="element-12235">{children}</AuthContext.Provider>;
}